import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import Thead from '../../../../../../core/components/table-components/Thead';
import Tr from '../../../../../../core/components/table-components/Tr';
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks';
import ThWithControls from '../../../../../../core/components/table-components/ThWithControls';
import { SearchTableFilterType } from '../../../../../../core/types/coreTypes';
import {
  rectificationsBySupplierFiltersSelector,
  reportIsVisibleSelector,
} from '../../../../../../store/selectors/reportsSelectors';
import { setRectificationsBySupplierFilters } from '../../../../../../store/slices/reportsSlice';
import {
  getRectificationsBySupplierReportExcelThunk,
  getRectificationsBySupplierReportThunk,
} from '../../../../../../store/thunks/reports/reportsThunks';
import { ICustomColumn } from '../../../../../../common/types/commonTypes';
import Dropdown from '../../../../../../core/components/dropdown/Dropdown';
import ThNew from '../../../../../../core/components/table-components/table-new/ThNew';
import { useRectificationsBySupplierReportRanges } from '../../../../../hooks/useRectificationsBySupplierReportRanges';

type RectificationsBySupplierColumnsProps = {
  columns: ICustomColumn[],
  totalElements: number,
}

const RectificationsBySupplierColumns: React.FC<RectificationsBySupplierColumnsProps> = ({ columns, totalElements }) => {
  const dispatch = useAppDispatch();
  const reportIsVisible = useAppSelector(reportIsVisibleSelector);
  const filters = useAppSelector(rectificationsBySupplierFiltersSelector);

  const { range, setRange, rangePatterns } = useRectificationsBySupplierReportRanges();

  const [searchVl, setSearchVl] = useState<SearchTableFilterType>({
    partCode: filters.partCode,
    partName: filters.partName,
  });

  const sortHandle = (field: number) => {
    if (filters.order?.field === field) {
      const newFilters = {
        ...filters,
        order: { ...filters.order, isAsc: !filters.order.isAsc },
      };
      dispatch(setRectificationsBySupplierFilters(newFilters));
      dispatch(getRectificationsBySupplierReportThunk({ filters: newFilters }));
    } else {
      const newFilters = {
        ...filters,
        order: { field, isAsc: true },
      };
      dispatch(setRectificationsBySupplierFilters(newFilters));
      dispatch(getRectificationsBySupplierReportThunk({ filters: newFilters }));
    }
  };

  const onChangeSearchHandle = (v: string, field: string) => {
    setSearchVl({ ...searchVl, [field]: v });
    if (!v) {
      const newFilters = { ...filters, [field]: undefined };
      dispatch(setRectificationsBySupplierFilters(newFilters));
      dispatch(getRectificationsBySupplierReportThunk({ filters: newFilters }));
    }
  };
  const searchPressHandle = (field: string) => {
    const newFilters = { ...filters, [field]: searchVl[field]?.trim(), page: 1 };
    dispatch(setRectificationsBySupplierFilters(newFilters));
    dispatch(getRectificationsBySupplierReportThunk({ filters: newFilters }));
  };

  const setRangeHandle = (field: string, value: [number, number]) => {
    const [min, max] = value;
    const newFilters = {
      ...filters,
      [field]: { min, max },
      page: 1,
    };
    dispatch(setRectificationsBySupplierFilters(newFilters));
    dispatch(getRectificationsBySupplierReportThunk({ filters: newFilters }));
  };

  const exportToExcel = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...rest } = filters;
    dispatch(getRectificationsBySupplierReportExcelThunk({ filters: rest }));
  };

  return (
    <Thead>
      <Tr>
        {columns.map((col) => (
          <ThWithControls
            key={col.dataIndex}
            col={col}
            tableFilters={filters}
            sortHandle={sortHandle}
            setRangeHandle={setRangeHandle}
            range={range}
            rangePatterns={rangePatterns}
            rangeStep={1}
            setRange={setRange}
            onChangeSearchHandle={onChangeSearchHandle}
            searchPressHandle={searchPressHandle}
            searchVl={searchVl}
            totalElements={totalElements}
            className={`rectificationsBySupplierReportTh--${col.dataIndex}`}
            disabledFilter={!reportIsVisible}
          />
        ))}
        <ThNew action>
          <Dropdown
            disabled={!totalElements}
            menuItems={[
              {
                label: 'Export to Excel',
                icon: <FontAwesomeIcon icon={faFileExcel} />,
                key: 'export-to-excel',
                onClick: exportToExcel,
              },
            ]}
            icon={<FontAwesomeIcon icon={faGear} />}
          />
        </ThNew>
      </Tr>
    </Thead>
  );
};

export default RectificationsBySupplierColumns;
